import { useEffect, useState } from "react";
import { X } from "lucide-react";
import dayjs from "dayjs";
import { SheetModal } from "./SheetModal";

export type DateRange = { from: string | null; to: string | null };

function getWeekPresets() {
  const today = dayjs();
  const dow = today.day(); // 0=Sun … 6=Sat
  const daysToSunday = dow === 0 ? 0 : 7 - dow;
  const thisSunday = today.add(daysToSunday, "day");
  const nextMonday = thisSunday.add(1, "day");
  const nextSunday = thisSunday.add(7, "day");
  // Weekend = Sat + Sun. If today is Sun, weekend is just today; if Sat, today + tomorrow.
  const daysToSat = dow === 0 ? 0 : dow === 6 ? 0 : 6 - dow;
  const thisSat = today.add(daysToSat, "day");
  const thisWeekendSun = dow === 0 ? today : thisSat.add(1, "day");
  return {
    thisWeek:    { from: today.format("YYYY-MM-DD"),    to: thisSunday.format("YYYY-MM-DD") },
    nextWeek:    { from: nextMonday.format("YYYY-MM-DD"), to: nextSunday.format("YYYY-MM-DD") },
    thisWeekend: { from: thisSat.format("YYYY-MM-DD"),  to: thisWeekendSun.format("YYYY-MM-DD") },
  };
}

export function formatDateRange(from: string | null, to: string | null): string {
  if (!from && !to) return "Date";
  const fmt = (d: string) => dayjs(d).format("MMM D");
  if (from && to) return `${fmt(from)} – ${fmt(to)}`;
  if (from) return `From ${fmt(from)}`;
  return `Until ${fmt(to!)}`;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  from: string | null;
  to: string | null;
  onChange: (from: string | null, to: string | null) => void;
}

export function DateFilterModal({ isOpen, onClose, from, to, onChange }: Props) {
  const [draftFrom, setDraftFrom] = useState(from ?? "");
  const [draftTo, setDraftTo] = useState(to ?? "");

  useEffect(() => {
    if (isOpen) {
      setDraftFrom(from ?? "");
      setDraftTo(to ?? "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const { thisWeek, nextWeek, thisWeekend } = getWeekPresets();
  const isThisWeek    = draftFrom === thisWeek.from    && draftTo === thisWeek.to;
  const isNextWeek    = draftFrom === nextWeek.from    && draftTo === nextWeek.to;
  const isThisWeekend = draftFrom === thisWeekend.from && draftTo === thisWeekend.to;

  const selectPreset = (preset: { from: string; to: string }, active: boolean) => {
    if (active) {
      setDraftFrom("");
      setDraftTo("");
    } else {
      setDraftFrom(preset.from);
      setDraftTo(preset.to);
    }
  };

  const hasValue = draftFrom !== "" || draftTo !== "";
  const today = dayjs().format("YYYY-MM-DD");

  return (
    <SheetModal isOpen={isOpen} onClose={onClose} className="sm:max-w-sm">
      <div className="px-5 pt-5 pb-4 flex items-center gap-3 border-b border-neutral-200 dark:border-neutral-800 shrink-0">
        <h2 className="text-lg font-semibold flex-1">Date</h2>
        <button
          type="button"
          onClick={onClose}
          className="p-1.5 rounded-full hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors text-neutral-500"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="px-4 pt-4 pb-2 flex flex-wrap gap-2">
        {[
          { label: "This week",    preset: thisWeek,    active: isThisWeek },
          { label: "This weekend", preset: thisWeekend, active: isThisWeekend },
          { label: "Next week",    preset: nextWeek,    active: isNextWeek },
        ].map(({ label, preset, active }) => (
          <button
            key={label}
            type="button"
            onClick={() => selectPreset(preset, active)}
            className={`
              inline-flex items-center px-3 py-1.5 rounded-full text-sm border transition-all
              ${active
                ? "bg-primary-500 border-primary-500 text-white shadow-sm"
                : "bg-neutral-100 dark:bg-neutral-800 border-neutral-200 dark:border-neutral-700 hover:border-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20"
              }
            `}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="px-4 py-3 grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-neutral-400 dark:text-neutral-500 uppercase tracking-wide">
            From
          </label>
          <input
            type="date"
            value={draftFrom}
            min={today}
            max={draftTo || undefined}
            onChange={(e) => setDraftFrom(e.target.value)}
            className="w-full px-3 py-2 rounded-xl bg-neutral-100 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 text-sm outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-500"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-neutral-400 dark:text-neutral-500 uppercase tracking-wide">
            To
          </label>
          <input
            type="date"
            value={draftTo}
            min={draftFrom || today}
            onChange={(e) => setDraftTo(e.target.value)}
            className="w-full px-3 py-2 rounded-xl bg-neutral-100 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 text-sm outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-500"
          />
        </div>
      </div>

      <div className="px-4 pb-5 pt-3 flex gap-2 border-t border-neutral-200 dark:border-neutral-800 shrink-0">
        {hasValue && (
          <button
            type="button"
            onClick={() => { setDraftFrom(""); setDraftTo(""); }}
            className="btn btn-secondary"
          >
            Clear
          </button>
        )}
        <button
          type="button"
          onClick={() => { onChange(draftFrom || null, draftTo || null); onClose(); }}
          className="btn btn-primary flex-1"
        >
          Done
        </button>
      </div>
    </SheetModal>
  );
}
